import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMutation } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import { Button } from '@/components/ui/button'
import { CloudUpload, Trash2, Loader2, Images } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useModuleActions } from '@/hooks/useMyPermissions'
import PhotoPickerDialog from '@/pages/photos/components/PhotoPickerDialog'

import * as Mutation from '../../../api/gql/mutation'
import { useUpload } from '../../../components/file-manager/hooks/useUpload'
import MeetingFileRow, { MeetingFile } from './MeetingFileRow'

interface Props {
  meetingId: string
  files: MeetingFile[]
  // Binds the resulting file set to the owning entity (setMeetingFiles /
  // setAgendaItemFiles). Awaited so the dropzone stays disabled until the
  // relation is actually persisted.
  onFilesChange: (fileIds: string[]) => Promise<any> | void
  disabled?: boolean
  compact?: boolean
  // Enables "From gallery" (existing Photos of this project) when the user can view Photos.
  projectId?: string
  allowUpload?: boolean
}

const MeetingFileUpload: React.FC<Props> = ({
  meetingId,
  files,
  onFilesChange,
  disabled = false,
  compact = false,
  projectId,
  allowUpload = true,
}) => {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const { canView: canViewPhotos } = useModuleActions('PHOTOS', projectId)
  const showGallery = !!projectId && canViewPhotos && !disabled

  const [upload, uploadState] = useUpload() as any
  const { uploads } = uploadState

  const [createFile] = useMutation(Mutation.CREATE_FILE)
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED)
  const [deleteFile] = useMutation(Mutation.DELETE_FILE)

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (disabled || busy || acceptedFiles.length === 0) return

      setBusy(true)
      try {
        const pending = await Promise.all(
          acceptedFiles.map(async (file) => {
            const { data } = await createFile({
              variables: {
                input: {
                  filename: file.name,
                  size: file.size,
                  mimetype: file.type || 'application/octet-stream',
                  encoding: 'utf8',
                  meetingId,
                },
              },
            })

            return {
              file,
              id: data.createFile.file.id,
              name: file.name,
              signedUrl: data.createFile.uploadUrl,
            }
          }),
        )

        const result = await upload(pending)

        // useUpload resolves to { cancelled, fileIds } instead of an id array
        // when the transfer was aborted — drop the orphaned File rows.
        if (!Array.isArray(result)) {
          const orphans = result?.fileIds || []
          if (orphans.length) await deleteFile({ variables: { ids: orphans } })
          return
        }

        await setFileUploaded({ variables: { ids: result } })
        await onFilesChange([...files.map((f) => f.id), ...result])
      } catch (e: any) {
        toastr.error(t('Common_error'), e.message)
      } finally {
        setBusy(false)
      }
    },
    [
      disabled,
      busy,
      meetingId,
      files,
      createFile,
      upload,
      deleteFile,
      setFileUploaded,
      onFilesChange,
      t,
    ],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disabled || busy,
  })

  const handleDelete = async (fileId: string) => {
    if (disabled || deletingId) return
    setDeletingId(fileId)
    try {
      await onFilesChange(files.filter((f) => f.id !== fileId).map((f) => f.id))
      await deleteFile({ variables: { ids: [fileId] } })
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    } finally {
      setDeletingId(null)
    }
  }

  const pickFromGallery = async (photos: Array<{ fileId: string }>) => {
    if (busy || photos.length === 0) return
    setBusy(true)
    try {
      await onFilesChange([...files.map((f) => f.id), ...photos.map((p) => p.fileId)])
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
      throw e
    } finally {
      setBusy(false)
    }
  }

  const uploadRows = Object.values(uploads) as any[]

  return (
    <div className="space-y-2">
      {!disabled && allowUpload && (
        <div
          {...getRootProps()}
          className={cn(
            'rounded-md border-2 border-dashed text-center transition-colors cursor-pointer',
            compact ? 'p-3' : 'p-6',
            isDragActive ? 'border-primary bg-primary/5' : 'border-border',
            busy && 'opacity-50 cursor-not-allowed',
          )}
        >
          <input {...getInputProps()} />
          <div className={cn('flex items-center justify-center gap-2', !compact && 'flex-col')}>
            {busy ? (
              <Loader2
                className={cn('animate-spin text-primary', compact ? 'h-4 w-4' : 'h-8 w-8')}
              />
            ) : (
              <CloudUpload
                className={cn(
                  compact ? 'h-4 w-4' : 'h-8 w-8',
                  isDragActive ? 'text-primary' : 'text-muted-foreground',
                )}
              />
            )}
            <p className={cn('mb-0', compact ? 'text-xs' : 'text-sm')}>
              {busy
                ? t('Meeting_files_uploading')
                : isDragActive
                  ? t('Meeting_files_drop_here')
                  : t('Meeting_files_drag_or_click')}
            </p>
          </div>
        </div>
      )}

      {showGallery && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8"
          disabled={busy}
          onClick={() => setPickerOpen(true)}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Images className="h-4 w-4" />}
          {t('Photos_from_gallery')}
        </Button>
      )}
      {showGallery && projectId && (
        <PhotoPickerDialog
          projectId={projectId}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          excludeFileIds={files.map((f) => f.id)}
          onPick={pickFromGallery}
        />
      )}

      {busy && uploadRows.length > 0 && (
        <div className="space-y-2">
          {uploadRows.map((info, index) => (
            <div key={index}>
              <div className="flex justify-between text-xs mb-1">
                <span className="truncate">{info.name}</span>
                <span className="shrink-0 pl-2">
                  {info.loaded} / {info.total}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${info.progress}%` }} // ui-drift: dynamic
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((f) => (
            <MeetingFileRow
              key={f.id}
              file={f}
              action={
                disabled ? null : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDelete(f.id)}
                    disabled={!!deletingId}
                    title={t('Common_delete')}
                  >
                    {deletingId === f.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default MeetingFileUpload
